import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  DollarSign, 
  MapPin, 
  Car, 
  Wrench, 
  CheckCircle, 
  FileText,
  MessageSquare,
  Image,
  Package,
  Receipt,
  Star,
  Shield,
  User,
  Phone,
  ChevronRight,
  Download,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const PHOTO_CAPTIONS = { antes: 'Antes del servicio', durante: 'Durante el trabajo', despues: 'Después del servicio' };

const ServiceRequestDetail = () => {
  const { API, sessionToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const { requestId } = useParams();
  
  const [request, setRequest] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [bids, setBids] = useState([]);
  const [acceptedBid, setAcceptedBid] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [taller, setTaller] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [completion, setCompletion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequestDetails();
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      
      // Obtener la solicitud
      const requestRes = await axios.get(`${API}/service-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setRequest(requestRes.data);

      // Obtener detalles reales de completitud (desglose, notas del taller, garantía)
      if (requestRes.data.status === 'completed') {
        try {
          const compRes = await axios.get(`${API}/service-requests/${requestId}/completion`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          });
          setCompletion(compRes.data);
        } catch (e) {
          // No completion data - non-critical
        }
      }

      // Obtener vehículo
      if (requestRes.data.vehicle_id) {
        try {
          const vehicleRes = await axios.get(`${API}/vehicles/${requestRes.data.vehicle_id}`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          });
          setVehicle(vehicleRes.data);
        } catch (e) {
          // Vehicle not found - non-critical error
        }
      }

      // Obtener bids
      try {
        const bidsRes = await axios.get(`${API}/bids?service_request_id=${requestId}`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        setBids(bidsRes.data);
        
        // Encontrar el bid aceptado
        const accepted = bidsRes.data.find(b => b.status === 'accepted');
        setAcceptedBid(accepted);

        // Si hay bid aceptado, obtener info del taller
        if (accepted) {
          try {
            const tallerRes = await axios.get(`${API}/public/talleres/${accepted.taller_id}`);
            setTaller(tallerRes.data);
          } catch (e) {
            // Taller not found - non-critical error
          }
        }
      } catch (e) {
        // No bids found - non-critical error
      }

      // Obtener cita si existe
      try {
        const appointmentsRes = await axios.get(`${API}/appointments`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        const relatedAppointment = appointmentsRes.data.find(a => a.service_request_id === requestId);
        setAppointment(relatedAppointment);

        // Obtener fotos reales del servicio
        if (relatedAppointment) {
          try {
            const photosRes = await axios.get(`${API}/appointments/${relatedAppointment.id}/photos`, {
              headers: { Authorization: `Bearer ${sessionToken}` }
            });
            setPhotos(photosRes.data);
          } catch (e) {
            // No photos - non-critical error
          }
        }
      } catch (e) {
        // No appointment found - non-critical error
      }

    } catch (error) {
      console.error('Error fetching request details:', error);
      toast.error('Error al cargar los detalles');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'open': 'Abierta',
      'in_progress': 'En Progreso',
      'completed': 'Completada',
      'cancelled': 'Cancelada'
    };
    return labels[status] || status;
  };

  const getUrgencyLabel = (urgency) => {
    const labels = {
      'urgent': { label: 'Urgente', color: 'bg-red-100 text-red-700' },
      'normal': { label: 'Normal', color: 'bg-yellow-100 text-yellow-700' },
      'flexible': { label: 'Flexible', color: 'bg-green-100 text-green-700' }
    };
    return labels[urgency] || { label: urgency, color: 'bg-slate-100 text-slate-700' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Detalles reales del servicio completado (desglose guardado en la transacción)
  const breakdown = completion?.breakdown;
  const completedServiceDetails = request?.status === 'completed' ? {
    services_performed: completion?.services_provided || [
      { name: request?.service_type || 'Servicio', description: request?.description, price: acceptedBid?.price || 0 }
    ],
    parts_used: breakdown?.parts || [],
    labor_cost: breakdown?.labor_cost ?? null,
    completion_date: completion?.created_at || appointment?.scheduled_date || request?.created_at,
    workshop_notes: breakdown?.workshop_notes || null,
    warranty: breakdown?.warranty || null,
    next_service_date: breakdown?.next_service_date || null,
    total_amount: completion?.amount ?? acceptedBid?.price ?? 0,
    commission: completion?.commission ?? Math.round((acceptedBid?.price || 0) * 0.04),
    payment_status: completion?.payment_status || 'paid',
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando detalles...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Solicitud no encontrada</h2>
          <p className="text-slate-600 mb-4">No se pudo encontrar la solicitud solicitada</p>
          <Button onClick={() => navigate('/client/dashboard')}>
            Volver al Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/client/dashboard')}
                className="hover:bg-purple-100"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                  Detalle de Solicitud
                </h1>
                <p className="text-sm text-slate-500">ID: {request.id?.slice(0, 8)}...</p>
              </div>
            </div>
            <Badge className={`${getStatusColor(request.status)} border px-4 py-2 text-sm`}>
              {getStatusLabel(request.status)}
            </Badge>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Columna Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info de la Solicitud */}
            <Card className="border-slate-200 overflow-hidden">
              <div className="bg-purple-600 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{request.service_type}</h2>
                    <div className="flex items-center space-x-3 mt-2">
                      <Badge className={getUrgencyLabel(request.urgency).color}>
                        {getUrgencyLabel(request.urgency).label}
                      </Badge>
                      <span className="text-purple-200 text-sm flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(request.created_at)}
                      </span>
                    </div>
                  </div>
                  {request.bids_count > 0 && (
                    <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
                      <p className="text-2xl font-bold text-white">{request.bids_count}</p>
                      <p className="text-xs text-purple-200">ofertas</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-sm font-medium text-slate-500 uppercase mb-2">Descripción del Problema</h3>
                <p className="text-slate-700 bg-slate-50 rounded-lg p-4">{request.description}</p>

                {/* Fechas preferidas */}
                {request.preferred_dates && request.preferred_dates.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-slate-500 uppercase mb-3">Fechas Preferidas</h3>
                    <div className="flex flex-wrap gap-2">
                      {request.preferred_dates.map((date) => (
                        <Badge key={`date-${date}`} className="bg-purple-100 text-purple-700">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(date)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ubicaciones */}
                {request.preferred_locations && request.preferred_locations.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-slate-500 uppercase mb-3">Ubicaciones de Preferencia</h3>
                    <div className="space-y-2">
                      {request.preferred_locations.map((loc, idx) => (
                        <div key={`loc-${loc.address || loc.name}-${idx}`} className="flex items-center space-x-3 bg-slate-50 rounded-lg p-3">
                          <Badge className={idx === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'}>
                            {idx + 1}°
                          </Badge>
                          <MapPin className="w-4 h-4 text-purple-600" />
                          <div>
                            <p className="font-medium text-slate-900">{loc.name || loc.type}</p>
                            <p className="text-sm text-slate-600">{loc.address}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Detalles del Servicio Completado */}
            {request.status === 'completed' && completedServiceDetails && (
              <>
                {/* Servicios Realizados */}
                <Card className="border-green-200 bg-green-50/30">
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <h3 className="text-lg font-semibold text-slate-900">Servicios Realizados</h3>
                    </div>
                    
                    <div className="space-y-3">
                      {completedServiceDetails.services_performed.map((service) => (
                        <div key={`service-${service.name}`} className="bg-white rounded-lg p-4 border border-green-100">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-slate-900">{service.name}</h4>
                              <p className="text-sm text-slate-600 mt-1">{service.description}</p>
                            </div>
                            <span className="font-bold text-green-600">{formatPrice(service.price)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>

                {/* Insumos y Repuestos */}
                {completedServiceDetails.parts_used.length > 0 && (
                <Card className="border-slate-200">
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <Package className="w-6 h-6 text-purple-600" />
                      <h3 className="text-lg font-semibold text-slate-900">Insumos y Repuestos Utilizados</h3>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Descripción</th>
                            <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Cantidad</th>
                            <th className="text-right py-3 px-2 text-sm font-medium text-slate-500">Precio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {completedServiceDetails.parts_used.map((part) => (
                            <tr key={`part-${part.name}`} className="border-b border-slate-100">
                              <td className="py-3 px-2 text-slate-900">{part.name}</td>
                              <td className="py-3 px-2 text-center text-slate-600">{part.quantity}</td>
                              <td className="py-3 px-2 text-right font-medium text-slate-900">{formatPrice(part.price)}</td>
                            </tr>
                          ))}
                          {completedServiceDetails.labor_cost !== null && (
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-2 text-slate-900">Mano de obra</td>
                              <td className="py-3 px-2 text-center text-slate-600">-</td>
                              <td className="py-3 px-2 text-right font-medium text-slate-900">{formatPrice(completedServiceDetails.labor_cost)}</td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr className="bg-purple-50">
                            <td colSpan="2" className="py-3 px-2 font-semibold text-slate-900">TOTAL</td>
                            <td className="py-3 px-2 text-right font-bold text-purple-600 text-lg">
                              {formatPrice(completedServiceDetails.total_amount)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </Card>
                )}

                {/* Comentarios del Taller */}
                {completedServiceDetails.workshop_notes && (
                <Card className="border-slate-200">
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <MessageSquare className="w-6 h-6 text-purple-600" />
                      <h3 className="text-lg font-semibold text-slate-900">Comentarios del Taller</h3>
                    </div>
                    
                    <div className="bg-purple-50 rounded-lg p-4 border border-slate-200">
                      <p className="text-slate-700 italic">"{completedServiceDetails.workshop_notes}"</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      {completedServiceDetails.warranty && (
                        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                          <p className="text-xs text-green-600 font-medium uppercase mb-1">Garantía</p>
                          <p className="font-semibold text-green-800">{completedServiceDetails.warranty}</p>
                        </div>
                      )}
                      {completedServiceDetails.next_service_date && (
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                          <p className="text-xs text-blue-600 font-medium uppercase mb-1">Próximo Servicio Recomendado</p>
                          <p className="font-semibold text-blue-800">{formatDate(completedServiceDetails.next_service_date)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
                )}

                {/* Fotos de Validación */}
                <Card className="border-slate-200">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <Image className="w-6 h-6 text-purple-600" />
                        <h3 className="text-lg font-semibold text-slate-900">Fotos de Validación</h3>
                      </div>
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verificado por taller
                      </Badge>
                    </div>
                    
                    {photos.length > 0 ? (
                      <div className="grid md:grid-cols-3 gap-4" data-testid="service-photos-grid">
                        {photos.map((photo) => (
                          <div key={photo.id} className="group relative">
                            <a href={`${API}/photos/${photo.id}`} target="_blank" rel="noreferrer">
                              <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                                <img
                                  src={`${API}/photos/${photo.id}`}
                                  alt={`Foto ${photo.photo_type}`}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                  loading="lazy"
                                />
                              </div>
                            </a>
                            <p className="text-sm text-slate-600 text-center mt-2">
                              {PHOTO_CAPTIONS[photo.photo_type] || photo.photo_type}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg py-8 text-center" data-testid="no-photos-placeholder">
                        <Image className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">El taller aún no ha subido fotos de este servicio</p>
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-500 mt-4 flex items-center">
                      <Shield className="w-3 h-3 mr-1" />
                      Las fotos son subidas por el taller como evidencia del trabajo realizado
                    </p>
                  </div>
                </Card>

                {/* Resumen de Transacción */}
                <Card className="border-slate-200 bg-slate-50">
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <Receipt className="w-6 h-6 text-purple-600" />
                      <h3 className="text-lg font-semibold text-slate-900">Resumen de Transacción</h3>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <div className="space-y-3">
                          {completedServiceDetails.parts_used.length > 0 && (
                            <div className="flex justify-between items-center py-2 border-b border-slate-100">
                              <span className="text-slate-600">Repuestos e insumos</span>
                              <span className="font-medium">
                                {formatPrice(completedServiceDetails.parts_used.reduce((sum, p) => sum + p.price, 0))}
                              </span>
                            </div>
                          )}
                          {completedServiceDetails.labor_cost !== null && (
                            <div className="flex justify-between items-center py-2 border-b border-slate-100">
                              <span className="text-slate-600">Mano de obra</span>
                              <span className="font-medium">{formatPrice(completedServiceDetails.labor_cost)}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center py-2 border-b border-slate-100">
                            <span className="text-slate-600">Comisión Flix (4%) — incluida</span>
                            <span className="font-medium text-purple-600">
                              {formatPrice(completedServiceDetails.commission)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-3 bg-purple-100 rounded-lg px-3">
                            <span className="font-semibold text-slate-900">TOTAL PAGADO</span>
                            <span className="font-bold text-xl text-purple-700">
                              {formatPrice(completedServiceDetails.total_amount)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                          <p className="text-xs text-green-600 font-medium uppercase mb-1">Estado del Pago</p>
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="font-semibold text-green-800">
                              {completedServiceDetails.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                            </span>
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                          <p className="text-xs text-slate-500 font-medium uppercase mb-1">Fecha de Pago</p>
                          <p className="font-medium text-slate-900">{formatDateTime(completedServiceDetails.completion_date)}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 border border-slate-200">
                          <p className="text-xs text-purple-600 font-medium uppercase mb-1">Registrado en</p>
                          <div className="flex items-center space-x-2">
                            <Shield className="w-5 h-5 text-purple-600" />
                            <span className="font-semibold text-purple-800">Car Passport</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </>
            )}

            {/* Lista de Ofertas (si hay) */}
            {bids.length > 0 && request.status !== 'completed' && (
              <Card className="border-slate-200">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Ofertas Recibidas ({bids.length})</h3>
                  <div className="space-y-3">
                    {bids.map((bid) => (
                      <div key={bid.id} className={`p-4 rounded-lg border ${bid.status === 'accepted' ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-lg text-purple-600">{formatPrice(bid.price)}</p>
                            <p className="text-sm text-slate-600">{bid.message}</p>
                            <p className="text-xs text-slate-500 mt-1">Tiempo estimado: {bid.estimated_time}</p>
                          </div>
                          <Badge className={bid.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                            {bid.status === 'accepted' ? 'Aceptada' : bid.status === 'rejected' ? 'Rechazada' : 'Pendiente'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Columna Lateral */}
          <div className="space-y-6">
            {/* Info del Vehículo */}
            {vehicle && (
              <Card className="border-slate-200">
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Car className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-slate-900">Vehículo</h3>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center">
                      <Car className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{vehicle.make} {vehicle.model}</p>
                      <p className="text-sm text-slate-600">{vehicle.year} • {vehicle.color}</p>
                      <p className="text-xs text-purple-600 font-medium">{vehicle.license_plate}</p>
                    </div>
                  </div>

                  {request.status === 'completed' && (
                    <Button 
                      variant="outline" 
                      className="w-full mt-4 border-purple-200 text-purple-600"
                      onClick={() => navigate(`/client/car-passport/${vehicle.id}`)}
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Ver Car Passport
                    </Button>
                  )}
                </div>
              </Card>
            )}

            {/* Info del Taller (si aceptó) */}
            {taller && (
              <Card className="border-slate-200">
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Wrench className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-slate-900">Taller Asignado</h3>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center">
                      <Wrench className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{taller.company_name || taller.name}</p>
                      <div className="flex items-center mt-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="ml-1 text-sm font-medium">{taller.rating?.toFixed(1) || '0.0'}</span>
                        <span className="ml-1 text-xs text-slate-500">({taller.total_reviews || 0} reseñas)</span>
                      </div>
                      {taller.location?.city && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />
                          {taller.location.city}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full border-purple-200 text-purple-600"
                      onClick={() => navigate(`/taller/${taller.id}`)}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Ver Perfil Completo
                    </Button>
                    {taller.phone && (
                      <Button variant="outline" className="w-full border-green-200 text-green-600">
                        <Phone className="w-4 h-4 mr-2" />
                        Contactar
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Timeline de Estado */}
            <Card className="border-slate-200">
              <div className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Estado del Proceso</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Solicitud Creada</p>
                      <p className="text-xs text-slate-500">{formatDate(request.created_at)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bids.length > 0 ? 'bg-green-100' : 'bg-slate-100'}`}>
                      {bids.length > 0 ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-slate-400" />}
                    </div>
                    <div>
                      <p className={`font-medium ${bids.length > 0 ? 'text-slate-900' : 'text-slate-400'}`}>Ofertas Recibidas</p>
                      <p className="text-xs text-slate-500">{bids.length} ofertas</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${acceptedBid ? 'bg-green-100' : 'bg-slate-100'}`}>
                      {acceptedBid ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-slate-400" />}
                    </div>
                    <div>
                      <p className={`font-medium ${acceptedBid ? 'text-slate-900' : 'text-slate-400'}`}>Oferta Aceptada</p>
                      {acceptedBid && <p className="text-xs text-slate-500">{formatPrice(acceptedBid.price)}</p>}
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${appointment ? 'bg-green-100' : 'bg-slate-100'}`}>
                      {appointment ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-slate-400" />}
                    </div>
                    <div>
                      <p className={`font-medium ${appointment ? 'text-slate-900' : 'text-slate-400'}`}>Cita Programada</p>
                      {appointment && <p className="text-xs text-slate-500">{formatDate(appointment.scheduled_date)}</p>}
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${request.status === 'completed' ? 'bg-green-100' : 'bg-slate-100'}`}>
                      {request.status === 'completed' ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-slate-400" />}
                    </div>
                    <div>
                      <p className={`font-medium ${request.status === 'completed' ? 'text-slate-900' : 'text-slate-400'}`}>Servicio Completado</p>
                      {request.status === 'completed' && <p className="text-xs text-green-600">✓ Registrado en Car Passport</p>}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestDetail;
